import React, { useState, useEffect } from 'react'
import { MaterialReactTable } from 'material-react-table'
import { Box , Snackbar, Alert } from '@mui/material'
import * as XLSX from 'xlsx'
import { getDesviacionCombinada, getDesviacionCombinadaNS, updateDesviacionCombinadaNS, calcularDesviacionCombinada } from 'src/services/motosSnopServices'
import { createColumns } from './columns'
import TableToolbar from './components/TableToolbar'
import { containerStyles } from './styles'

const DesviacionContainer = () => {
  const [desviacionData, setDesviacionData] = useState([])
  const [loading, setLoading] = useState(true)
  const [editedRows, setEditedRows] = useState(new Set())
  const [originalValues, setOriginalValues] = useState(new Map())
  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [alertSeverity, setAlertSeverity] = useState('success')

  // Función para cargar los datos iniciales
  const getDesviacionCombinadafetch = async () => {
    try {
      setLoading(true)
      const response = await getDesviacionCombinada()
      
      if (response && response.desviacion_combinada) {
        // Organizar datos por modelo
        const dataByModel = {}
        
        response.desviacion_combinada.forEach(item => {
          const modelo = item.Modelo_Planeacion
          
          if (!dataByModel[modelo]) {
            dataByModel[modelo] = []
          }
          dataByModel[modelo].push(item)
        })

        // Crear datos finales según la lógica de nivel_seleccionado
        const finalData = Object.keys(dataByModel).map((modelo, index) => {
          const items = dataByModel[modelo]
          
          // Buscar el item con nivel_seleccionado === 1 (para NS escogido - verde)
          const itemNSEscogido = items.find(item => item.nivel_seleccionado === 1)
          
          // El otro item será para Optimizando costos (naranja)
          // Si hay itemNSEscogido, usar el otro; si no, usar el único existente
          const itemOptimizandoCostos = itemNSEscogido 
            ? items.find(item => item.nivel_seleccionado !== 1) || items[0]
            : items[0]
          
          // Si no hay itemNSEscogido, usar el mismo para ambos
          const registroEscogido = itemNSEscogido || itemOptimizandoCostos
          const registroOptimo = itemOptimizandoCostos

          return {
            id: index + 1,
            modeloPlaneacion: modelo,
            planVentas: registroEscogido.Plan_de_ventas || 0,
            ventasPromedioDiarias: registroEscogido.Ventas_promedio_ventas_diarias || 0,
            desviacionVentasDiaria: registroEscogido.Desviacion_estandar_ventas_diarias || 0,
            tiempoPromedioEntrega: registroEscogido.Tiempo_promedio_tiempo_entrega || 0,
            desviacionTiempoEntrega: registroEscogido.Desviacion_estandar_tiempo_entrega || 0,
            desviacionCombinada: registroEscogido.Desviacion_combinada || 0,
            // Datos del "Optimizando costos" (naranja) - del registro que NO tiene nivel_seleccionado === 1
            nsOptimo: registroOptimo.Nivel_servicio || 0,
            diasInventarioOptimo: registroOptimo.Dias_Inventario || 0,
            costoOptimo: registroOptimo.Suma_Costos || 0,
            inventarioMedioOptimo: registroOptimo.Inventario_medio || 0,
            // Datos del "NS escogido" (verde) - del registro con nivel_seleccionado === 1, o del único si no hay
            nsEscogido: registroEscogido.Nivel_servicio || 0,
            diasInventarioEscogido: registroEscogido.Dias_Inventario || 0,
            costoEscogido: registroEscogido.Suma_Costos || 0,
            inventarioMedioEscogido: registroEscogido.Inventario_medio || 0,
            // Campos adicionales
            factorK: registroEscogido.Factor_K || 0,
            stockSeguridad: registroEscogido.Stock_de_seguridad || 0,
            utilidad: registroEscogido.Utilidad || 0,
            costoMoto: registroEscogido.Costo_moto || 0,
            costoInventario: registroEscogido.Costo_inventario || 0,
            costoAlmacenamientoMoto: registroEscogido.Costo_almacenamiento_moto || 0,
            tipoRegistro: itemNSEscogido ? 'Escogido' : 'Óptimo',
          }
        })

        setDesviacionData(finalData)
      }
    } catch (error) {
      console.error('Error al obtener datos de desviación:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getDesviacionCombinadafetch()
  }, [])

  // Función para manejar la edición del NS
  const handleNSEdit = async (newValue, rowData, column) => {
    try {
      const nivelServicio = parseFloat(newValue)
      const { modeloPlaneacion } = rowData
      
      // Llamar al endpoint
      const response = await getDesviacionCombinadaNS(nivelServicio, modeloPlaneacion)
      
      // Validar que el endpoint trae la información necesaria
      if (response && response.desviacion_combinada && response.desviacion_combinada.length > 0) {
        const responseData = response.desviacion_combinada[0]
        
        // Verificar que los campos necesarios estén presentes
        if (!responseData.Dias_Inventario || !responseData.Suma_Costos || !responseData.Inventario_medio) {
          return
        }
        
        // Guardar valores originales si es la primera edición
        if (!originalValues.has(modeloPlaneacion)) {
          const originalItem = desviacionData.find(item => item.modeloPlaneacion === modeloPlaneacion)
          setOriginalValues(prev => new Map(prev).set(modeloPlaneacion, {
            nsEscogido: originalItem.nsEscogido,
            diasInventarioEscogido: originalItem.diasInventarioEscogido,
            costoEscogido: originalItem.costoEscogido,
            inventarioMedioEscogido: originalItem.inventarioMedioEscogido,
          }))
        }

        // Actualizar los datos localmente con la respuesta del endpoint
        const updatedData = desviacionData.map(item => {
          if (item.modeloPlaneacion === modeloPlaneacion) {
            return {
              ...item,
              [column]: nivelServicio,
              // Actualizar con los valores del endpoint
              diasInventarioEscogido: responseData.Dias_Inventario,
              costoEscogido: responseData.Suma_Costos,
              inventarioMedioEscogido: responseData.Inventario_medio,
            }
          }
          return item
        })
        
        setDesviacionData(updatedData)
        
        // Marcar la fila como editada
        setEditedRows(prev => new Set(prev).add(modeloPlaneacion))
      } else {
        // Si no hay respuesta válida del endpoint
        alert('Error al escoger el NS')
      }
      
    } catch (error) {
      console.error('Error al actualizar NS:', error)
      alert('Error al escoger el NS')
    }
  }

  // Función para restaurar valores originales
  const handleRestore = (modeloPlaneacion) => {
    const originalData = originalValues.get(modeloPlaneacion)
    if (!originalData) return

    // Restaurar los datos a los valores originales
    const restoredData = desviacionData.map(item => {
      if (item.modeloPlaneacion === modeloPlaneacion) {
        return {
          ...item,
          nsEscogido: originalData.nsEscogido,
          diasInventarioEscogido: originalData.diasInventarioEscogido,
          costoEscogido: originalData.costoEscogido,
          inventarioMedioEscogido: originalData.inventarioMedioEscogido,
        }
      }
      return item
    })

    setDesviacionData(restoredData)

    // Quitar de las filas editadas
    setEditedRows(prev => {
      const newSet = new Set(prev)
      newSet.delete(modeloPlaneacion)
      return newSet
    })

    // Quitar de los valores originales
    setOriginalValues(prev => {
      const newMap = new Map(prev)
      newMap.delete(modeloPlaneacion)
      return newMap
    })
  }

  // Función para restaurar todos los valores editados
  const handleRestoreAll = () => {
    const editedCount = editedRows.size
    if (editedCount === 0) return

    const confirmed = window.confirm(`¿Estás seguro de que quieres restaurar todos los ${editedCount} NS editados a sus valores originales?`)
    if (!confirmed) return

    // Crear nueva data con todos los valores restaurados
    const restoredData = desviacionData.map(item => {
      if (editedRows.has(item.modeloPlaneacion)) {
        const originalData = originalValues.get(item.modeloPlaneacion)
        if (originalData) {
          return {
            ...item,
            nsEscogido: originalData.nsEscogido,
            diasInventarioEscogido: originalData.diasInventarioEscogido,
            costoEscogido: originalData.costoEscogido,
            inventarioMedioEscogido: originalData.inventarioMedioEscogido,
          }
        }
      }
      return item
    })

    setDesviacionData(restoredData)
    setEditedRows(new Set())
    setOriginalValues(new Map())
  }

  // Función para guardar todos los NS editados
  const handleSaveAll = async () => {
    const editedCount = editedRows.size
    if (editedCount === 0) return

    const confirmed = window.confirm(`¿Estás seguro de que quieres guardar todos los ${editedCount} NS editados?`)
    if (!confirmed) return

    try {
      // Preparar los datos para enviar al endpoint
      const editedData = []
      editedRows.forEach(modeloPlaneacion => {
        const item = desviacionData.find(d => d.modeloPlaneacion === modeloPlaneacion)
        if (item) {
          editedData.push({
            modelo_planeacion: item.modeloPlaneacion,
            nivel_servicio: item.nsEscogido
          })
        }
      })

      // Llamar al endpoint PUT
      const response = await updateDesviacionCombinadaNS(editedData)
      
      if (response) {
        // Éxito - recargar toda la data
        alert('NS guardados exitosamente')
        
        // Resetear estados de edición
        setEditedRows(new Set())
        setOriginalValues(new Map())
        
        // Recargar todo el componente (más simple y consistente)
        getDesviacionCombinadafetch()
      } else {
        alert('Error al guardar los NS. Inténtalo de nuevo.')
      }
    } catch (error) {
      alert('Error al guardar los NS. Inténtalo de nuevo.')
    }
  }

  // Función para exportar a Excel con headers agrupados
  const handleExportToExcel = (tableInstance) => {
    const visibleColumns = tableInstance.getVisibleLeafColumns().filter(
      col => col.columnDef.header !== 'Última modificación'
    )

    // 1. Construir arrays de headers principales y subheaders
    const mainHeaders = []
    const subHeaders = []
    const merges = []
    let colIndex = 0
    let lastGroup = null
    let groupStart = 0

    visibleColumns.forEach((column) => {
      // Buscar el grupo principal (header padre)
      let groupHeader = ''
      let { parent } = column
      while (parent) {
        if (parent.columnDef && parent.columnDef.header) {
          groupHeader = parent.columnDef.header
          break
        }
        parent = parent.parent
      }
      if (!groupHeader) groupHeader = column.columnDef.header
      subHeaders.push(column.columnDef.header)
      // Si es el primero o cambia el grupo, cerrar el anterior y abrir uno nuevo
      if (lastGroup === null) {
        lastGroup = groupHeader
        groupStart = colIndex
      } else if (groupHeader !== lastGroup) {
        // Fusionar celdas del grupo anterior si abarca más de una columna
        if (colIndex - groupStart > 1) {
          merges.push({ s: { r: 0, c: groupStart }, e: { r: 0, c: colIndex - 1 } })
        }
        // Si solo es una columna, no fusionar
        lastGroup = groupHeader
        groupStart = colIndex
      }
      mainHeaders.push(groupHeader)
      colIndex += 1
    })
    // Fusionar el último grupo si abarca más de una columna
    if (colIndex - groupStart > 1) {
      merges.push({ s: { r: 0, c: groupStart }, e: { r: 0, c: colIndex - 1 } })
    }
    // Para columnas individuales, poner merge de una celda a sí misma (opcional)
    let prev = null
    mainHeaders.forEach((g, i) => {
      if ((i === 0 || g !== prev) && (i === mainHeaders.length - 1 || g !== mainHeaders[i + 1])) {
        merges.push({ s: { r: 0, c: i }, e: { r: 0, c: i } })
      }
      prev = g
    })

    // 2. Preparar los datos para exportar
    const exportData = tableInstance.getFilteredRowModel().rows.map(row =>
      visibleColumns.map(column => row.getValue(column.id))
    )

    // 3. Crear worksheet con sheet_add_aoa
    const aoa = [mainHeaders, subHeaders, ...exportData]
    const worksheet = XLSX.utils.aoa_to_sheet(aoa)
    worksheet['!merges'] = merges

    // 4. Crear y exportar el archivo
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Desviación Combinada')
    XLSX.writeFile(workbook, 'Desviacion_Combinada.xlsx')
  }

  // Función para calcular desviación combinada
  const handleCalcular = async () => {
    try {
      const response = await calcularDesviacionCombinada()
      console.log('Respuesta calcular:', response)
      
      // Recargar los datos de la tabla después del cálculo exitoso
      await getDesviacionCombinadafetch()
      
      setAlertMessage('Cálculo realizado exitosamente')
      setAlertSeverity('success')
      setAlertOpen(true)
    } catch (error) {
      setAlertMessage('Error al calcular desviación combinada')
      setAlertSeverity('error')
      setAlertOpen(true)
    }
  }

  const columns = createColumns(handleNSEdit, handleRestore, editedRows)

  return (
    <Box>
      <MaterialReactTable 
        columns={columns} 
        data={desviacionData}
        state={{
          isLoading: loading,
        }}
        enableDensityToggle={false}
        density="spacious"
        enablePagination
        enableSorting
        enableEditing
        editDisplayMode="cell"
        enableColumnActions={false}
        enableStickyHeader
        getRowCanExpand={() => false}
        renderTopToolbarCustomActions={({ table }) => (
          <TableToolbar
            editedRows={editedRows}
            onSaveAll={handleSaveAll}
            onRestoreAll={handleRestoreAll}
            onExportToExcel={() => handleExportToExcel(table)}
            onCalcular={handleCalcular}
          />
        )}
        muiTableContainerProps={{
          sx: containerStyles.tableContainer,
        }}
        muiTableBodyRowProps={{
          sx: containerStyles.tableRow,
        }}
        muiTablePaperProps={{
          elevation: 0,
          sx: containerStyles.tablePaper,
        }}
        initialState={{
          pagination: { pageSize: 50, pageIndex: 0 },
        }}
      />
      <Snackbar
        open={alertOpen}
        autoHideDuration={3000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setAlertOpen(false)} severity={alertSeverity} sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default DesviacionContainer 